import {
  WSButton,
  WSElement,
  WSGrid,
  WSInfoBox,
  WSMessageBox,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { WSQueries } from "../../../query/WSQuery";
import { usePayeeTaxForms } from "../../../query/taxForm/queries/usePayeeTaxForms";
import { useBrowserPageTitle } from "../../../shared/hooks/useBrowserPageTitle";
import { openIntercom } from "../../../shared/utils/intercom";
import { FAQ1099 } from "../../1099NECFiling/components/FAQ1099";
import { TaxFormPreview } from "./components/TaxForm";
import { useUserId } from "../../../query/hooks/helpers";
import { useMemberProfile } from "../../../query/users/queries";
import { useCurrentTaxDocumentsYear } from "../../TaxDocuments/utils/useCurrentTaxDocumentsYear";
import { TAX_DOCUMENTS_ROOT_PATH } from "../../TaxDocuments/paths";
import { SelectTaxYear } from "../../TaxDocuments/components/SelectYear";

export const RouteIndexOld: React.FC<RouteComponentProps> = () => {
  const history = useHistory();
  const currentYear = useCurrentTaxDocumentsYear();

  useBrowserPageTitle(`${currentYear} Tax Documents`);

  const userId = useUserId();
  const queryMember = useMemberProfile(userId);
  const queryPayerTaxForms = usePayeeTaxForms({ year: currentYear });

  return (
    <WSPage>
      <WSPageToolbar title="Tax Documents" titleAfter={<SelectTaxYear />} />

      <WSInfoBox my="XL" size="M">
        Note: While we are upgrading the tax filing experience for 2024,
        previous years 1099 interfaces may differ from the new experience. View
        the current tax filing experience on your new{" "}
        <a target="_self" onClick={() => history.push(TAX_DOCUMENTS_ROOT_PATH)}>
          2024 dashboard
        </a>
        .
      </WSInfoBox>

      <WSGrid>
        <WSGrid.Item span={{ m: "8" }}>
          <WSQueries queries={{ queryPayerTaxForms, queryMember }}>
            {({ queryPayerTaxFormsData: taxForms, queryMemberData: member }) =>
              taxForms.length > 0 ? (
                <>
                  {taxForms.map(taxForm => (
                    <TaxFormPreview
                      key={taxForm.taxFormId}
                      mb="XL"
                      taxForm={taxForm}
                      member={member}
                    />
                  ))}
                </>
              ) : (
                <WSPanel>
                  <WSMessageBox.Warning
                    title="No Form 1099 exists"
                    icon="alert-circle"
                  >
                    <WSText.ParagraphSm>
                      If you do not expect a Form 1099 for 2023, then you’re all
                      set.
                    </WSText.ParagraphSm>

                    <WSText.ParagraphSm mt="M" mb="XL">
                      If you were expecting a Form 1099 for 2023, here are a few
                      things you can do:
                    </WSText.ParagraphSm>
                    <ul style={{ listStyleType: "decimal" }}>
                      <WSElement as="li" mb="M">
                        <WSText.ParagraphSm weight="medium">
                          Check your sign-up or sign-in email
                        </WSText.ParagraphSm>
                        <WSText.ParagraphSm mt="XS">
                          A Form 1099 is issued to the email that the payer has
                          on file. Confirm the email and try signing up or
                          signing in again.
                        </WSText.ParagraphSm>
                      </WSElement>
                      <li>
                        <WSText.ParagraphSm weight="medium">
                          Need more help?
                        </WSText.ParagraphSm>
                        <WSText.ParagraphSm mt="XS">
                          If you’ve confirmed your email and you’re still
                          getting this message, please contact Wingspan support
                          below and have the payer name(s) ready so we can
                          assist you.
                        </WSText.ParagraphSm>
                      </li>
                    </ul>
                  </WSMessageBox.Warning>

                  <WSButton.Primary mt="XL" onClick={openIntercom}>
                    Contact Wingspan support
                  </WSButton.Primary>
                </WSPanel>
              )
            }
          </WSQueries>
        </WSGrid.Item>
        <WSGrid.Item span={{ m: "4" }}>
          <WSElement>
            <FAQ1099 mb="2XL" />
            <WSElement>
              <WSText weight="medium">Need additional support?</WSText>
              <WSButton.Link onClick={openIntercom}>
                Chat with a live representative
              </WSButton.Link>
            </WSElement>
          </WSElement>
        </WSGrid.Item>
      </WSGrid>
    </WSPage>
  );
};
